// The FAQ: its content as written in code (defined in @gaintplay/db so the seed can copy it) and
// the rules every answer edited in Admin → FAQ must follow. The API enforces them on save; the
// admin uses the same functions to explain a problem before saving.
//
// Answers are plain text, one paragraph: the same words go on /faq, into its FAQPage structured
// data and into the support assistant's instructions, so nothing in them may be markup, a hidden
// character, a link to another site or a phone number.
import { foreignAddress, phoneNumber } from './links.js';

export * from '@gaintplay/db/faq';

/** Lengths in characters, after spaces are tidied (tidyFaqText). */
export const FAQ_LIMITS = {
  question: { min: 10, max: 150 },
  answer: { min: 40, max: 1000 },
  title: { min: 2, max: 60 },
  slug: { min: 2, max: 60 },
  linkLabel: { min: 2, max: 40 },
  linkTo: { min: 2, max: 120 },
} as const;
export type FaqTextField = 'question' | 'answer' | 'title' | 'linkLabel';

/**
 * How big the FAQ may grow. Every published answer also goes into each support assistant call
 * (its help centre), so the page stays a page, not a knowledge base.
 */
export const FAQ_MAX = { groups: 20, items: 200 } as const;

/** Lowercase letters and digits in words joined by single hyphens, e.g. pending-coins. */
export const FAQ_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** The id of the /faq page's data block with the live FAQ (apps/web pages/info/faq-markup.ts). */
export const FAQ_DATA_ID = 'gp-faq';

/**
 * Every id the /faq page has besides its anchors: the app's shell (apps/web index.html,
 * components/), the page (pages/info) and its data block. Topic headings are `<slug>-title`, so no
 * anchor may end in "-title" either. apps/api/test/faq.test.ts reads those files and fails when an
 * id there is missing here.
 */
export const FAQ_PAGE_IDS: readonly string[] = [
  'root',
  'main',
  'faq',
  'topics',
  FAQ_DATA_ID,
  'still-stuck',
  'keep-reading',
  'account-menu',
  'account-sheet-title',
  'app-update-title',
  // The installed app's launch splash (apps/web/index.html).
  'gp-splash',
  'pf-name',
  'pf-name-hint',
  'legacy-sheet-title',
  'legacy-email-sheet',
  'legacy-title',
  'legacy-email',
  'r-community',
  'r-extras',
  'wall-modal-title',
];

/** One paragraph: line breaks and runs of spaces become single spaces. */
export const tidyFaqText = (s: string) => s.replace(/\s+/g, ' ').trim();

/** Control, zero-width and direction-override characters (as lib/prompt-data.ts strips). */
const INVISIBLE =
  /[\u0000-\u0008\u000b-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff]/;
const HTML_TAG = /<\/?[a-z!][^>]*>/i;
const HTML_ENTITY = /&(?:[a-z]+|#\d+|#x[0-9a-f]+);/i;
const MARKDOWN_LINK = /\[[^\]]*\]\([^)]*\)/;
const MARKDOWN = /\*\*|__|`|^#{1,6}\s/;

const LABEL: Record<FaqTextField, string> = {
  question: 'The question',
  answer: 'The answer',
  title: 'The topic name',
  linkLabel: 'The link text',
};

/** Why `text` can't be saved as this field, or null. Pass it through tidyFaqText first. */
export function faqTextProblem(text: string, field: FaqTextField): string | null {
  const { min, max } = FAQ_LIMITS[field];
  const what = LABEL[field];
  if (text.length < min) return `${what} needs at least ${min} characters.`;
  if (text.length > max) return `${what} can be ${max} characters at most (it has ${text.length}).`;
  return faqContentProblem(text, field);
}

/**
 * faqTextProblem without the length limits: what any FAQ text must be (plain, no foreign links or
 * phone numbers). Translations use it, with room for languages that run longer than English.
 */
export function faqContentProblem(text: string, field: FaqTextField): string | null {
  const what = LABEL[field];
  if (INVISIBLE.test(text))
    return `${what} has invisible characters (pasted text sometimes carries them). Type it again.`;
  if (HTML_TAG.test(text)) return `${what} must be plain text: no HTML tags.`;
  if (HTML_ENTITY.test(text))
    return `${what} must be plain text: write the character itself, not a code like &amp;.`;
  if (MARKDOWN_LINK.test(text))
    return `${what} must be plain text: put a link in the link fields below, not in the text.`;
  if (MARKDOWN.test(text))
    return `${what} must be plain text: no Markdown such as **bold**, \`code\` or # headings.`;
  const foreign = foreignAddress(text);
  if (foreign)
    return `${what} mentions ${foreign.slice(0, 60)}: only gaintplay.com addresses can appear in the FAQ.`;
  const phone = phoneNumber(text);
  if (phone) return `${what} has a phone number (${phone.slice(0, 30)}): the FAQ can’t list one.`;
  return null;
}

/** Why `slug` can't be an anchor on /faq, or null. Uniqueness is checked against the database. */
export function faqSlugProblem(slug: string): string | null {
  const { min, max } = FAQ_LIMITS.slug;
  if (slug.length < min || slug.length > max)
    return `The anchor needs ${min} to ${max} characters.`;
  if (!FAQ_SLUG.test(slug))
    return 'The anchor can only have lowercase letters, digits and single hyphens, like pending-coins.';
  if (FAQ_PAGE_IDS.includes(slug) || slug.endsWith('-title'))
    return `The page already uses “${slug}” for itself. Pick another anchor.`;
  return null;
}

/** Why `to` can't be an answer's link, or null: it must be a page on this site. */
export function faqLinkProblem(to: string): string | null {
  const { min, max } = FAQ_LIMITS.linkTo;
  if (
    to.length < min ||
    to.length > max ||
    !/^\/[a-z0-9-]+(?:\/[a-z0-9-]+)*\/?(?:#[a-z0-9-]+)?$/.test(to)
  )
    return 'The link must be a page on this site, like /gift-cards or /faq#pending-coins.';
  return null;
}

/** An anchor made from a question: "Why are coins pending?" → "why-are-coins-pending". */
export function faqSlugFrom(text: string): string {
  const words = text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[’']/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  let slug = '';
  for (const w of words) {
    const next = slug ? `${slug}-${w}` : w;
    if (next.length > 48) break;
    slug = next;
  }
  return slug || 'answer';
}

/**
 * `base`, or `base-2`, `base-3`… (shortened to fit the anchor limit), whichever isn't in `taken`
 * and is a valid anchor.
 */
export function freeFaqSlug(base: string, taken: ReadonlySet<string>): string {
  const room = (suffix: string) =>
    base.slice(0, FAQ_LIMITS.slug.max - suffix.length).replace(/-+$/, '') + suffix;
  for (let n = 1; ; n++) {
    const slug = room(n === 1 ? '' : `-${n}`);
    if (!taken.has(slug) && !faqSlugProblem(slug)) return slug;
  }
}
